import type { CollectionSummaryJSONObject } from '@ikegps/core-api';
import { action, computed, observable, makeObservable } from 'mobx';
import { AutoCompleteMenu, type SelectAutoCompleteOption } from '../../components/autocomplete.menu/autocomplete.menu';
import { Input } from '../../components/input/input';
import { InputState } from '../../components/input/input.state';
import type { MenuItem } from '../../components/menu/menu.item';
import { ModalFormComponent, type ModalFormComponentCtx } from '../../components/modal.form/modal.form.component';
import { Spinner } from '../../components/spinner/spinner';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { Services } from '../../services';
import { IkeSort } from '../../sort';
import { userDisplayName } from '../../util/userDisplayName';
import { CSS, Style } from './user.assign.modal.style';
import { UserCard, type UserCardCtx, UserCardUnassigned } from './user.card';

addStyles(Style);

const USER_ID_UNASSIGN = '$user_unassign$';

export class UserAssignModal extends ModalFormComponent {
	private collections: CollectionSummaryJSONObject[];

	@observable private userId: string | null = null;
	@observable private assign: boolean = true;

	private inputState: InputState;
	private menu: AutoCompleteMenu;

	constructor(ctx: ModalFormComponentCtx, collections: CollectionSummaryJSONObject[]) {
		super(ctx);
		makeObservable(this);

		this.collections = collections;

		this.inputState = new InputState(T.ROLE_USER);
		this.addState(this.inputState);

		this.menu = new AutoCompleteMenu({
			inputState: this.inputState,
			options: () => this.userOptions,
			optionView: (option: SelectAutoCompleteOption, nameParts: MithrilVNode[]) => this.viewUserCard(option, nameParts),
			onselect: (option) => this.selectUser(option),
		});
	}

	@computed get loadingUsers(): boolean {
		const users = Services.UserProfile.list;
		return users == null || users.length === 0;
	}

	@computed get userOptions(): SelectAutoCompleteOption[] {
		if (this.loadingUsers) {
			return [];
		}

		const options: SelectAutoCompleteOption[] = [
			{
				id: USER_ID_UNASSIGN,
				value: Translate(T.COLLECTION_UNASSIGNED),
			},
		];

		const users = Services.UserProfile.list;
		const sortedUsers = IkeSort.byKey(users.slice(), ['firstname', 'lastname', 'username'], IkeSort.ASC);
		for (const user of sortedUsers) {
			const displayName = userDisplayName(user);
			options.push({
				id: user.objectId,
				value: displayName,
				searchValue: displayName !== user.username ? `${displayName} ${user.username}` : undefined,
			});
		}

		return options;
	}

	get nodes(): MithrilVNode {
		if (this.loadingUsers) {
			return m('div', { className: CSS.container }, Spinner.view());
		}

		return m('div', { className: CSS.container }, [
			m('div', { className: CSS.content }, [
				m('p', Translate(T.COLLECTION_ASSIGN_CONFIRM, { count: this.collections.length })),
				this.viewInput(),
			]),
			m('div', { className: CSS.menu }, this.menu.view()),
		]);
	}

	private viewInput(): MithrilVNode {
		if (!this.assign) {
			return UserCardUnassigned.viewChip({
				cancel: () => this.selectUser(),
			});
		}

		if (this.userId == null) {
			return Input.view(this.inputState);
		}

		const user = Services.UserProfile.get(this.userId);
		return UserCard.viewChip(user, {
			cancel: () => this.selectUser(),
		});
	}

	protected override canClose() {
		return super.canClose() && !this.menu.isOpen();
	}

	private viewUserCard(option: SelectAutoCompleteOption, nameParts: MithrilVNode[]): MenuItem | null {
		if (option.id === USER_ID_UNASSIGN) {
			return {
				view: UserCardUnassigned.view(nameParts),
				action: () => this.unassignUser(),
			};
		}

		const user = Services.UserProfile.get(option.id);
		if (user == null) {
			return null;
		}

		const me = Services.User.current;
		const cardCtx: UserCardCtx = {
			me: me != null && user.objectId === me.objectId,
		};

		return {
			view: UserCard.view(user, nameParts, cardCtx),
			action: () => this.selectUser(option),
		};
	}

	override validate() {
		if (!this.assign) {
			this.inputState.setError('');
			return;
		}

		if (this.userId == null) {
			this.inputState.setError(T.USER_ASSIGN_REQUIRED);
		} else {
			this.inputState.setError('');
		}
	}

	override async submit() {
		await Services.Collection.assign(this.collections, this.assign && this.userId ? this.userId : null);
	}

	@action private selectUser(option?: SelectAutoCompleteOption) {
		if (option != null) {
			this.userId = option.id;
			this.inputState.setValue(option.value);
			this.inputState.setDisabled(true);
		} else {
			this.userId = null;
			this.inputState.setValue('');
			this.inputState.setDisabled(false);
		}
		this.assign = true;
	}

	@action private unassignUser() {
		this.selectUser(null);
		this.assign = false;
	}
}
